import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { ProjectFocusItem } from "services/project-history.service";
import {
  patchProjectActivity,
  ProjectActivity,
} from "services/project-activities.service";
import { ProjectFocusListSkeleton } from "components/project/project-tab-skeletons/project-tab-skeletons";
import ProjectActivityCard from "components/project/project-activity-card";
import ProjectActivityEditModal from "components/project/project-activity-edit-modal";
import { formatPrettyDate } from "utils/helperFunctions";

type Props = {
  companyId: string;
  projectId: string;
  items: ProjectFocusItem[];
  loading?: boolean;
  cardName?: string;
  onChanged?: () => void;
};

export default function ProjectFocusPanel({
  companyId,
  projectId,
  items,
  loading,
  cardName,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState<ProjectActivity | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggleDone = async (activity: ProjectActivity) => {
    setBusyId(activity.id);
    try {
      await patchProjectActivity(companyId, projectId, activity.id, {
        isDone: !activity.isDone,
      });
      onChanged?.();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not save activity.",
        })
      );
    } finally {
      setBusyId(null);
    }
  };

  const renderItem = (item: ProjectFocusItem) => {
    if (item.kind === "due_date") {
      const title = String(item.data.title || cardName || "Deal");
      return (
        <div
          key={item.id}
          className={`rounded-md border px-3 py-2.5 ${
            item.overdue
              ? "border-red-200 bg-red-50"
              : "border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)]"
          }`}
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--p-color-text)]">
            <Calendar size={14} />
            {t("project_focus_due_date", { defaultValue: "Due date" })}
            {item.overdue && (
              <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                {t("overdue", { defaultValue: "Overdue" })}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-[var(--p-color-text)]">{title}</div>
          <div className="text-xs text-[var(--p-color-text-secondary)]">
            {formatPrettyDate(item.at, "lt-LT", false)}
          </div>
        </div>
      );
    }

    const activity = item.data as unknown as ProjectActivity;
    return (
      <ProjectActivityCard
        key={item.id}
        activity={activity}
        busy={busyId === activity.id}
        className={
          item.overdue
            ? "border-amber-200 bg-amber-50"
            : undefined
        }
        trailing={
          item.overdue ? (
            <span className="font-semibold text-amber-700">
              {t("overdue", { defaultValue: "Overdue" })}
            </span>
          ) : null
        }
        onToggleDone={() => toggleDone(activity)}
        onEdit={() => setEditing(activity)}
      />
    );
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown size={16} className="shrink-0" />
        ) : (
          <ChevronRight size={16} className="shrink-0" />
        )}
        <span className="text-sm font-semibold text-[var(--p-color-text)]">
          {t("project_focus_title", { defaultValue: "Focus" })}
        </span>
        {!loading && items.length > 0 && (
          <span className="rounded-full bg-[var(--p-color-bg-surface-secondary)] px-2 py-0.5 text-xs font-medium text-[var(--p-color-text-secondary)]">
            {items.length}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-[var(--p-color-border)] px-4 py-3">
          {loading ? (
            <ProjectFocusListSkeleton rows={2} />
          ) : items.length === 0 ? (
            <p className="text-sm text-[var(--p-color-text-secondary)]">
              {t("project_focus_empty", {
                defaultValue:
                  "No focus items yet. Scheduled activities and due dates will appear here.",
              })}
            </p>
          ) : (
            <div className="flex flex-col gap-2 project-tab-fade-in">
              {items.map(renderItem)}
            </div>
          )}
        </div>
      )}

      <ProjectActivityEditModal
        open={!!editing}
        companyId={companyId}
        projectId={projectId}
        activity={editing}
        onClose={() => setEditing(null)}
        onSaved={() => onChanged?.()}
      />
    </div>
  );
}
